import React from 'react';
import { format } from 'date-fns';

import { ActionMenu } from '@lyearn/molecules';
import { DatePicker, Popover, Stack, Typography } from '@lyearn/ui';

import ClockEditIcon from '../ClockEditIcon';
import { getTimeSuggestions } from '../TimeSuggestions';

type TimeIntervalSelectorProps = {
  startTime: Date;
  endTime: Date;
  handleStartDateChange: (date: Date | null | undefined) => void;
  handleEndDateChange: (date: Date | null | undefined) => void;
  handleStartTimeSelect: (dateString: string) => void;
  handleEndTimeSelect: (dateString: string) => void;
  isEditMode?: boolean;
};

export const TimeIntervalSelector: React.FC<TimeIntervalSelectorProps> = ({
  startTime,
  endTime,
  handleStartDateChange,
  handleEndDateChange,
  handleStartTimeSelect,
  handleEndTimeSelect,
}) => {
  return (
    <Stack className="w-full px-12 mt-8" direction="row" gapX={8}>
      <Stack className="flex-shrink-0 mt-8">
        <ClockEditIcon />
      </Stack>
      <Stack alignItems="center" gapX={6}>
        {/*Start Date*/}
        <Popover>
          <Popover.Button>
            <Stack className="rounded-4 hover:bg-bg-hover cursor-pointer p-4">
              <Typography className="text-text-secondary" variant="body-short-02">
                {`${format(startTime, 'E, MMM d')}`}
              </Typography>
            </Stack>
          </Popover.Button>
          <Popover.Content>
            <Stack className="p-24">
              <DatePicker value={startTime} onChange={handleStartDateChange}></DatePicker>
            </Stack>
          </Popover.Content>
        </Popover>

        {/*Start Time*/}
        <ActionMenu
          items={getTimeSuggestions({
            referenceDate: startTime,
            showDuration: false,
          })}
          minWidth={25}
          overrides={{
            PopoverContent: {
              className: 'max-h-[180px] overflow-y-auto',
            },
          }}
          onAction={handleStartTimeSelect}>
          <Stack className="rounded-4 hover:bg-bg-hover cursor-pointer p-4">
            <Typography className="text-text-secondary" variant="body-short-02">
              {`${format(startTime, 'p')}`}
            </Typography>
          </Stack>
        </ActionMenu>

        <Typography className="text-text-secondary" variant="body-short-02">
          -
        </Typography>

        {/*End Time*/}
        <ActionMenu
          className="max-h-[250px] overflow-y-auto"
          items={getTimeSuggestions({
            referenceDate: endTime,
            baseTime: startTime,
            showDuration: true,
          })}
          minWidth={25}
          overrides={{
            PopoverContent: {
              className: 'max-h-[180px] overflow-y-auto',
            },
          }}
          onAction={handleEndTimeSelect}>
          <Stack className="rounded-4 hover:bg-bg-hover cursor-pointer p-4">
            <Typography className="text-text-secondary" variant="body-short-02">
              {`${format(endTime, 'p')}`}
            </Typography>
          </Stack>
        </ActionMenu>

        {/*End Date*/}
        <Popover>
          <Popover.Button>
            <Stack className="rounded-4 hover:bg-bg-hover cursor-pointer p-4">
              <Typography className="text-text-secondary" variant="body-short-02">
                {`${format(endTime, 'E, MMM d')}`}
              </Typography>
            </Stack>
          </Popover.Button>
          <Popover.Content>
            <Stack className="p-24">
              <DatePicker value={endTime} onChange={handleEndDateChange} />
            </Stack>
          </Popover.Content>
        </Popover>
      </Stack>
    </Stack>
  );
};
